import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';

const GOOGLE_COURSES_ENDPOINT = '/api/rosteradapter/v2/google/courses';
const RETURN_TO_QUERY_PARAM = 'returnTo';

export const GOOGLE_CONSENT_ERROR_QUERY_PARAM = 'googleConsentError';
export const GOOGLE_CONSENT_ACCOUNT_MISMATCH = 'account_mismatch';
export const GOOGLE_CONSENT_ACCOUNT_MISMATCH_MESSAGE =
  'Google account mismatch. Please continue with the same Google account you used to sign in.';

export const GOOGLE_IMPORT_SOURCE_QUERY_PARAM = 'googleImportSource';
export const GOOGLE_IMPORT_SOURCES = {
  drawer: 'drawer',
  dialog: 'dialog',
} as const;

const googleCourseSchema = z.object({
  course_id: z.string(),
  course_name: z.string(),
});

const googleCoursesSchema = z.array(googleCourseSchema);

export type GoogleCourse = z.infer<typeof googleCourseSchema>;
export type GoogleImportSource =
  (typeof GOOGLE_IMPORT_SOURCES)[keyof typeof GOOGLE_IMPORT_SOURCES];
export type UseGetGoogleCoursesApiResult = Omit<
  UseQueryResult<GoogleCoursesQueryData>,
  'data'
> & {
  data: GoogleCourse[];
  isRedirectingToConsent: boolean;
};

interface GoogleCoursesQueryData {
  courses: GoogleCourse[];
  isRedirectingToConsent: boolean;
}

function getGoogleCoursesEndpoint(source?: GoogleImportSource) {
  const currentUrl = new URL(window.location.href);

  if (source) {
    currentUrl.searchParams.set(GOOGLE_IMPORT_SOURCE_QUERY_PARAM, source);
  }

  const returnTo = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
  const queryParams = new URLSearchParams({
    [RETURN_TO_QUERY_PARAM]: returnTo.startsWith('/') ? returnTo : '/',
  });

  return `${GOOGLE_COURSES_ENDPOINT}?${queryParams.toString()}`;
}

async function getGoogleCourses(
  source: GoogleImportSource = GOOGLE_IMPORT_SOURCES.drawer
): Promise<GoogleCoursesQueryData> {
  const response = await fetch(getGoogleCoursesEndpoint(source), {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    redirect: 'manual',
  });

  if (response.type === 'opaqueredirect' || response.status === 303) {
    const consentUrl =
      response.headers.get('Location') ??
      response.headers.get('location') ??
      undefined;

    window.location.href = consentUrl ?? getGoogleCoursesEndpoint(source);
    return {
      courses: [],
      isRedirectingToConsent: true,
    };
  }

  if (!response.ok) {
    throw new Error(`Response was not ok - status ${response.status}`);
  }

  return {
    courses: googleCoursesSchema.parse(await response.json()),
    isRedirectingToConsent: false,
  };
}

const getGoogleCoursesApiQueryKey = (source: GoogleImportSource) =>
  ['googleCourses', source] as const;

export function useGetGoogleCoursesApi(
  enabled = true,
  source: GoogleImportSource = GOOGLE_IMPORT_SOURCES.drawer
): UseGetGoogleCoursesApiResult {
  const query = useQuery({
    queryKey: getGoogleCoursesApiQueryKey(source),
    queryFn: () => getGoogleCourses(source),
    enabled,
  });

  return {
    ...query,
    data: query.data?.courses ?? [],
    isRedirectingToConsent: query.data?.isRedirectingToConsent ?? false,
  };
}
